import type { WizardState } from './types';

export interface StepError {
  field: string;
  message: string;
  warning?: boolean; // if true, shown in banner but does NOT block Next
}

function required(value: any, field: string, label: string): StepError | null {
  if (value === undefined || value === null || value === '') {
    return { field, message: `${label} is required` };
  }
  return null;
}

function email(value: any, field: string, label: string): StepError | null {
  if (!value || typeof value !== 'string') return { field, message: `${label} is required` };
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return { field, message: `${label} must be a valid email` };
  return null;
}

function oneOf(value: any, allowed: string[], field: string, label: string): StepError | null {
  if (!value || !allowed.includes(value)) {
    return { field, message: `${label} must be one of: ${allowed.join(', ')}` };
  }
  return null;
}

function positiveInt(value: any, field: string, label: string): StepError | null {
  if (value === undefined || value === null || value === '') return { field, message: `${label} is required` };
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) return { field, message: `${label} must be a positive whole number` };
  return null;
}

function minLength(value: any, min: number, field: string, label: string): StepError | null {
  if (!value || typeof value !== 'string' || value.length < min) {
    return { field, message: `${label} must be at least ${min} characters` };
  }
  return null;
}

export function validateWizardStep(step: number, state: WizardState): StepError[] {
  const errors: StepError[] = [];
  const push = (e: StepError | null) => { if (e) errors.push(e); };

  switch (step) {
    case 1: {
      const info = state.departmentInfo || {};
      const addr = info.address || {};
      // Hard block — department name is the unique identifier (the "plug")
      push(minLength(info.name, 2, 'departmentInfo.name', 'Department name'));
      // Soft warnings — shown in banner but do not block Next
      const warn = (e: StepError | null) => { if (e) errors.push({ ...e, warning: true }); };
      warn(required(addr.zip, 'departmentInfo.address.zip', 'ZIP code'));
      // contactEmail removed - redundant with Step 3 contacts
      warn(required(info.timezone, 'departmentInfo.timezone', 'Timezone'));
      warn(required(info.departmentType, 'departmentInfo.departmentType', 'Department type'));
      warn(required(info.departmentClassification, 'departmentInfo.departmentClassification', 'Classification'));
      // jurisdiction fields removed - NERIS lookup data not loaded yet
      break;
    }

    case 2: {
      push(required(state.tier, 'tier', 'Tier'));
      break;
    }

    case 3: {
      const contacts = state.contacts || [];
      if (contacts.length === 0) {
        errors.push({ field: 'contacts', message: 'At least one contact is required' });
      } else {
        if (!contacts.some((c) => c.isPrimary)) {
          errors.push({ field: 'contacts', message: 'At least one contact must be marked as primary' });
        }
        contacts.forEach((c, i) => {
          push(required(c.name, `contacts[${i}].name`, `Contact ${i + 1} name`));
          if (!c.emails || c.emails.length === 0) {
            errors.push({ field: `contacts[${i}].emails`, message: `Contact ${i + 1} must have at least one email` });
          } else {
            c.emails.forEach((e, j) => {
              push(email(e.address, `contacts[${i}].emails[${j}].address`, `Contact ${i + 1} email`));
            });
          }
        });
      }
      break;
    }

    case 4: {
      const b = state.billing || {};
      push(required(b.billingEntity, 'billing.billingEntity', 'Billing entity'));
      push(required(b.subscriptionType, 'billing.subscriptionType', 'Subscription type'));
      push(required(b.startDate, 'billing.startDate', 'Start date'));
      push(required(b.billingCycle, 'billing.billingCycle', 'Billing cycle'));
      const bc = b.billingContact || {};
      push(required(bc.name, 'billing.billingContact.name', 'Billing contact name'));
      push(email(bc.email, 'billing.billingContact.email', 'Billing contact email'));

      if (b.subscriptionType === 'trial') {
        const t = state.trial || {};
        const dc = t.departmentContact || {};
        push(required(dc.name, 'trial.departmentContact.name', 'Trial contact name'));
        push(email(dc.email, 'trial.departmentContact.email', 'Trial contact email'));
      }
      break;
    }

    case 5: {
      const p = state.inspectionPlan || {};
      push(required(p.volumeBand, 'inspectionPlan.volumeBand', 'Volume band'));
      if (!p.maxSeriesPerYear || p.maxSeriesPerYear <= 0) {
        errors.push({ field: 'inspectionPlan.maxSeriesPerYear', message: 'Max series per year must be greater than 0' });
      }
      push(required(p.commitmentTerm, 'inspectionPlan.commitmentTerm', 'Commitment term'));
      push(required(p.overagePolicy, 'inspectionPlan.overagePolicy', 'Overage policy'));
      push(oneOf(p.source, ['department', 'organization'], 'inspectionPlan.source', 'Plan source'));
      break;
    }

    case 6: {
      // Regulatory Framework — all optional with defaults
      break;
    }

    case 7: {
      // Modules & Settings — optional with defaults, but an explicit selection
      // must leave at least one module enabled.
      const modules = Array.isArray(state.modules) ? state.modules : [];
      if (modules.length > 0 && !modules.some((m) => m && m.isEnabled)) {
        push({ field: 'modules', message: 'At least one module must be enabled' });
      }
      break;
    }

    case 8: {
      const auth = state.authenticationSettings || {};
      push(required(auth.authMethod, 'authenticationSettings.authMethod', 'Auth method'));

      if (auth.authMethod === 'saml') {
        const saml = auth.saml || {};
        push(required(saml.idpEntityId, 'authenticationSettings.saml.idpEntityId', 'IdP Entity ID'));
        push(required(saml.idpSsoUrl, 'authenticationSettings.saml.idpSsoUrl', 'IdP SSO URL'));
      }

      if (auth.authMethod === 'microsoft365') {
        const m365 = auth.microsoft365 || {};
        push(required(m365.tenantId, 'authenticationSettings.microsoft365.tenantId', 'Entra Tenant ID'));
      }

      const admin = state.initialAdmin || {};
      push(required(admin.firstName, 'initialAdmin.firstName', 'Admin first name'));
      push(required(admin.lastName, 'initialAdmin.lastName', 'Admin last name'));
      push(email(admin.email, 'initialAdmin.email', 'Admin email'));
      break;
    }

    case 9: {
      const ids = state.initialDataSetup?.inspectionAccounts ?? [];
      if (ids.length === 0) {
        errors.push({ field: 'initialDataSetup.inspectionAccounts', message: 'At least one inspection account is required' });
      } else {
        ids.forEach((acct, i) => {
          push(required(acct.name, `initialDataSetup.inspectionAccounts[${i}].name`, `Inspection account ${i + 1} name`));
        });
        if (!ids.some((a) => a.isDefault)) {
          errors.push({ field: 'initialDataSetup.inspectionAccounts', message: 'One inspection account must be set as default' });
        }
      }
      // Terms of Service validation
      if (!state.tosAcknowledged) {
        errors.push({ field: 'tosAcknowledged', message: 'You must acknowledge the Terms of Service' });
      }
      break;
    }

    case 10: {
      // Review & Create — UI-only recap of prior steps. TOS is validated at
      // step 9 (where the checkbox actually lives in InitialDataSetupStep).
      break;
    }

    default:
      break;
  }

  return errors;
}
